import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { useLanguage } from '@/contexts/LanguageContext';
import { Facebook, Instagram, MapPin, Phone, Mail } from 'lucide-react';
import { Separator } from '@/components/ui/separator';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = '/assets/images/logos/xy43zlm2_logo-belgo-831e91aa.avif';
const TRAITEUR_LOGO = '/assets/images/logos/56o24czl_Belgo_logo-traiteur_coul-fbfb2591.png';
const CAMION_LOGO = '/assets/images/logos/8f932y1p_LOGO_CAMION_BELGO_cuisine-urbaine_couleur_renv-2494dd8c.png';

export default function Footer() {
  const { t, lang } = useLanguage();
  const [info, setInfo] = useState({ global_address: '153 Champlain, Saint-Jean-sur-Richelieu, QC', global_phone: '+1 (450) 347-8635', global_email: 'info@restobelgo.com' });

  useEffect(() => {
    axios.get(`${API}/site-content/theme`).then(r => {
      setInfo({
        global_address: r.data.global_address || info.global_address,
        global_phone: r.data.global_phone || info.global_phone,
        global_email: r.data.global_email || info.global_email,
      });
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <footer className="bg-black text-white" data-testid="main-footer">
      {/* Logos Bar */}
      <div className="border-b border-white/10 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-center gap-8 sm:gap-16 flex-wrap">
          <img src={TRAITEUR_LOGO} alt="Traiteur Belgo" className="h-16 sm:h-20 w-auto object-contain" />
          <img src={LOGO_URL} alt="Resto Belgo" className="h-16 sm:h-20 w-auto object-contain" />
          <img src={CAMION_LOGO} alt="Camion Belgo" className="h-16 sm:h-20 w-auto object-contain" />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div>
            <span className="font-heading text-2xl font-bold text-[#f4d00d]">Resto Belgo</span>
            <p className="text-gray-400 text-sm leading-relaxed mt-4">
              {t('hero.tagline')}
            </p>
            <div className="flex gap-3 mt-6">
              <a href="https://www.facebook.com/restobelgotraiteurfoodtruck" target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg bg-white/10 hover:bg-[#e53a3a] transition-colors" data-testid="social-facebook">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="https://www.instagram.com/restobelgo_traiteur_foodtruck/" target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg bg-white/10 hover:bg-[#e53a3a] transition-colors" data-testid="social-instagram">
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-heading text-lg font-semibold mb-4 text-[#f4d00d]">{t('footer.quickLinks')}</h3>
            <nav className="flex flex-col gap-2">
              {[
                { to: '/menu', label: t('nav.menu') },
                { to: '/assets/files/pdf/cutp7udy_Belgo_menu-traiteur_2026-a9fa5ffe.pdf', label: t('nav.catering'), external: true },
                { to: '/assets/files/pdf/22z8rqf9_Belgo_menu-foodtruck_2026-80234699.pdf', label: t('nav.foodtruck'), external: true },
                { to: '/gallery', label: t('nav.gallery') },
                { to: '/about', label: t('nav.about') },
              ].map(link => link.external ? (
                <a key={link.to} href={link.to} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#f4d00d] text-sm transition-colors">
                  {link.label}
                </a>
              ) : (
                <Link key={link.to} to={link.to} className="text-gray-400 hover:text-[#f4d00d] text-sm transition-colors">
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Hours */}
          <div>
            <h3 className="font-heading text-lg font-semibold mb-4 text-[#f4d00d]">{t('contact.hours')}</h3>
            <div className="text-sm text-gray-400 space-y-1">
              <p>Lun - Mar: <span className="text-gray-500">Fermé / Closed</span></p>
              <p>Mer: <span className="text-white">11h - 20h</span></p>
	      <p>Jeu: <span className="text-white">11h - 21h</span></p>
              <p>Ven: <span className="text-white">11h - 22h</span></p>
              <p>Sam: <span className="text-white">11h - 21h</span></p>
              <p>Dim: <span className="text-white">11h - 21h</span></p>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-heading text-lg font-semibold mb-4 text-[#f4d00d]">{t('nav.contact')}</h3>
            <div className="space-y-3 text-sm text-gray-400">
              <a href={`https://maps.google.com/?q=${encodeURIComponent(info.global_address)}`} target="_blank" rel="noopener noreferrer" className="flex items-start gap-2 hover:text-[#f4d00d] transition-colors">
                <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-[#e53a3a]" />
                <span>{info.global_address}</span>
              </a>
              <a href={`tel:${info.global_phone.replace(/[^+\d]/g, '')}`} className="flex items-center gap-2 hover:text-[#f4d00d] transition-colors">
                <Phone className="w-4 h-4 shrink-0 text-[#e53a3a]" />
                <span>{info.global_phone}</span>
              </a>
              <a href={`mailto:${info.global_email}`} className="flex items-center gap-2 hover:text-[#f4d00d] transition-colors">
                <Mail className="w-4 h-4 shrink-0 text-[#e53a3a]" />
                <span>{info.global_email}</span>
              </a>
            </div>
          </div>
        </div>

        <Separator className="my-8 bg-white/10" />

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-500">
          <p>&copy; {new Date().getFullYear()} Resto Belgo. {t('footer.rights')}</p>
          <div className="flex items-center gap-4 flex-wrap justify-center">
            <Link to="/privacy" className="hover:text-gray-300 transition-colors" data-testid="privacy-footer-link">
              {t('nav.admin') === 'Admin' ? 'Confidentialité' : 'Privacy'}
            </Link>
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('belgo-open-cookie-settings'))}
              className="hover:text-gray-300 transition-colors"
              data-testid="cookie-settings-footer-link"
            >
              {t('nav.admin') === 'Admin' ? 'Témoins' : 'Cookies'}
            </button>
            <Link to="/admin/login" className="hover:text-gray-300 transition-colors" data-testid="admin-footer-link">
              {t('nav.admin')}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
